// Chit P&L — pick a chit, see its lifetime financials: revenue (collections +
// commission), payouts disbursed + pending, write-offs, net position.

import { useEffect, useState, type ReactElement } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { current_language } from '../i18n/index.js';
import { format_inr, short_month_label } from '../lib/format.js';
import { friendly_error } from '../lib/friendly-error.js';
import { orpc } from '../lib/orpc.js';
import { Icon } from '../components/Icon.js';

interface PnlData {
  chit: {
    name_ta: string;
    name_en: string;
    pool_size: number;
    duration_months: number;
    monthly_subscription_paise: number;
    foreman_commission_pct: number;
    start_date: string;
    status: string;
  };
  headline: {
    expected_total_paise: number;
    collected_total_paise: number;
    outstanding_paise: number;
    commission_earned_paise: number;
    payouts_disbursed_paise: number;
    payouts_pending_paise: number;
    write_offs_paise: number;
    net_position_paise: number;
    collection_rate: number;
    cycles_completed: number;
    cycles_total: number;
  };
  monthly: Array<{ due_month: string; expected_paise: number; collected_paise: number; commission_paise: number }>;
  payouts: Array<{
    cycle_index: number;
    cycle_date: string;
    receive_amount_paise: number;
    recipient_subscriber_id: string | null;
    recipient_name_en: string | null;
    recipient_name_ta: string | null;
    is_disbursed: boolean;
  }>;
}
interface ChitMatch {
  id: string;
  name_ta: string;
  name_en: string;
  status: string;
  pool_size: number;
  monthly_subscription_paise: number;
}

export function ReportsChitPnlPage(): ReactElement {
  const lang = current_language();
  const t = (ta: string, en: string): string => (lang === 'ta' ? ta : en);
  const [params, set_params] = useSearchParams();
  const [picked_id, set_picked_id] = useState<string | null>(params.get('chit_id'));
  const [q_text, set_q_text] = useState('');

  useEffect(() => {
    if (picked_id) params.set('chit_id', picked_id);
    else params.delete('chit_id');
    set_params(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked_id]);

  const search_q = useQuery({
    queryKey: ['report-chit-search', q_text],
    queryFn: () => orpc.reports.chit_search({ q: q_text }),
  });
  const matches = (search_q.data?.chits ?? []) as ChitMatch[];

  const pnl_q = useQuery({
    queryKey: ['report-chit-pnl', picked_id],
    queryFn: () => orpc.reports.chit_pnl({ chit_id: picked_id ?? '' }) as Promise<PnlData>,
    enabled: !!picked_id,
  });
  const data = pnl_q.data;

  const export_csv = (): void => {
    if (!data) return;
    const lines = [
      'section,key,value_or_amount',
      `chit,name,${(data.chit.name_en || data.chit.name_ta).replace(/,/g, ' ')}`,
      `chit,pool_size,${data.chit.pool_size}`,
      `chit,duration_months,${data.chit.duration_months}`,
      `chit,monthly_subscription_rupees,${(data.chit.monthly_subscription_paise / 100).toFixed(2)}`,
      `chit,commission_pct,${data.chit.foreman_commission_pct}`,
      `headline,expected_total_rupees,${(data.headline.expected_total_paise / 100).toFixed(2)}`,
      `headline,collected_total_rupees,${(data.headline.collected_total_paise / 100).toFixed(2)}`,
      `headline,commission_earned_rupees,${(data.headline.commission_earned_paise / 100).toFixed(2)}`,
      `headline,payouts_disbursed_rupees,${(data.headline.payouts_disbursed_paise / 100).toFixed(2)}`,
      `headline,payouts_pending_rupees,${(data.headline.payouts_pending_paise / 100).toFixed(2)}`,
      `headline,write_offs_rupees,${(data.headline.write_offs_paise / 100).toFixed(2)}`,
      `headline,net_position_rupees,${(data.headline.net_position_paise / 100).toFixed(2)}`,
      '',
      'monthly,month,expected,collected,commission',
      ...data.monthly.map((m) =>
        `monthly,${m.due_month},${(m.expected_paise / 100).toFixed(2)},${(m.collected_paise / 100).toFixed(2)},${(m.commission_paise / 100).toFixed(2)}`,
      ),
      '',
      'payouts,cycle,date,recipient,amount,disbursed',
      ...data.payouts.map((p) =>
        `payouts,${p.cycle_index},${p.cycle_date},${((p.recipient_name_en || p.recipient_name_ta) ?? '').replace(/,/g, ' ')},${(p.receive_amount_paise / 100).toFixed(2)},${p.is_disbursed}`,
      ),
    ];
    const blob = new Blob(['﻿', lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chit-pnl-${picked_id}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [preview, set_preview] = useState<string | null>(null);
  const pdf_m = useMutation({
    mutationFn: () => orpc.reports.chit_pnl_pdf({ chit_id: picked_id ?? '' }),
    onSuccess: (out) => {
      if (out.preview_html) set_preview(out.preview_html);
      else window.open(out.pdf_url, '_blank');
    },
  });

  return (
    <div className="page full">
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link to="/reports" style={{ color: 'var(--ink-3)', textDecoration: 'none', fontSize: 12 }}>
              ← {t('அறிக்கைகள்', 'Reports')}
            </Link>
          </div>
          <div className={'h-title ' + (lang === 'ta' ? 'tamil' : '')}>{t('சீட்டு லாபம்/நஷ்டம்', 'Chit P&L')}</div>
          <div className="h-sub">{data ? (lang === 'ta' ? data.chit.name_ta : data.chit.name_en) : t('சீட்டை தேர்வு செய்யவும்', 'Pick a chit')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={export_csv} disabled={!data}>
            <Icon.download /> CSV
          </button>
          <button type="button" className="btn primary" onClick={() => pdf_m.mutate()} disabled={!picked_id || pdf_m.isPending}>
            <Icon.download /> {pdf_m.isPending ? '…' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Chit picker */}
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ padding: 14 }}>
          <input
            type="search"
            placeholder={t('சீட்டை தேடவும்…', 'Search chits…')}
            value={q_text}
            onChange={(e) => set_q_text(e.target.value)}
            style={{ width: '100%', maxWidth: 480, padding: '8px 10px', fontSize: 14, border: '1px solid var(--line)', borderRadius: 4, fontFamily: 'inherit' }}
          />
          <div style={{ marginTop: 8, display: 'grid', gap: 4, maxHeight: 280, overflowY: 'auto', maxWidth: 640, gridTemplateColumns: '1fr 1fr' }}>
            {matches.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => set_picked_id(m.id)}
                style={{ display: 'flex', gap: 8, padding: '6px 8px', textAlign: 'left', background: m.id === picked_id ? 'var(--primary-soft)' : 'white', border: '1px solid var(--line)', borderRadius: 4, cursor: 'pointer' }}
              >
                <span className={lang === 'ta' ? 'tamil' : ''} style={{ flex: 1 }}>{(lang === 'ta' ? m.name_ta : m.name_en) || '—'}</span>
                <span className="mono" style={{ color: 'var(--ink-3)', fontSize: 11 }}>{m.pool_size}p · {format_inr(m.monthly_subscription_paise)}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {!picked_id && (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
          {t('சீட்டை தேர்வு செய்யவும்', 'Pick a chit to begin')}
        </div>
      )}

      {pnl_q.isLoading && <div style={{ padding: 20, color: 'var(--ink-3)' }}>{t('ஏற்றுகிறது…', 'Loading…')}</div>}
      {pnl_q.error && <div style={{ padding: 20, color: 'var(--bad)' }}>{friendly_error(pnl_q.error)}</div>}

      {data && (
        <>
          <div className="kpis" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
            <div className="kpi"><div className="kpi-label">{t('வசூல்', 'Collected')}</div><div className="kpi-value">{format_inr(data.headline.collected_total_paise)}</div><div className="kpi-sub">{(data.headline.collection_rate * 100).toFixed(1)}% {t('எதிர்பார்ப்பு', 'of expected')}</div></div>
            <div className={'kpi' + (data.headline.outstanding_paise > 0 ? ' warn' : '')}>
              <div className="kpi-label">{t('நிலுவை', 'Outstanding')}</div><div className="kpi-value">{format_inr(data.headline.outstanding_paise)}</div>
            </div>
            <div className="kpi primary"><div className="kpi-label">{t('கமிஷன்', 'Commission earned')}</div><div className="kpi-value">{format_inr(data.headline.commission_earned_paise)}</div><div className="kpi-sub">{data.chit.foreman_commission_pct}% {t('விகிதம்', 'rate')}</div></div>
            <div className={'kpi' + (data.headline.net_position_paise >= 0 ? '' : ' warn')}>
              <div className="kpi-label">{t('நிகர நிலை', 'Net position')}</div><div className="kpi-value">{format_inr(data.headline.net_position_paise)}</div>
            </div>
          </div>

          <div className="kpis" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi"><div className="kpi-label">{t('வழங்கப்பட்ட பணம்', 'Payouts disbursed')}</div><div className="kpi-value">{format_inr(data.headline.payouts_disbursed_paise)}</div><div className="kpi-sub">{data.headline.cycles_completed} / {data.headline.cycles_total} {t('சுழற்சிகள்', 'cycles')}</div></div>
            <div className={'kpi' + (data.headline.payouts_pending_paise > 0 ? ' warn' : '')}>
              <div className="kpi-label">{t('எதிர்கால பணம்', 'Payouts pending')}</div><div className="kpi-value">{format_inr(data.headline.payouts_pending_paise)}</div>
            </div>
            <div className={'kpi' + (data.headline.write_offs_paise > 0 ? ' warn' : '')}>
              <div className="kpi-label">{t('நிலுவை எழுதுதல்', 'Write-offs')}</div><div className="kpi-value">{format_inr(data.headline.write_offs_paise)}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {/* Monthly breakdown */}
            <div className="card">
              <div className="card-head"><div className="card-title">{t('மாதம் வாரியாக', 'Monthly breakdown')}</div></div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ background: 'var(--bg-elev)' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('மாதம்', 'Month')}</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('எதிர்பார்ப்பு', 'Expected')}</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('வசூல்', 'Collected')}</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('கமிஷன்', 'Commission')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.monthly.length === 0 ? (
                      <tr><td colSpan={4} style={{ padding: 14, textAlign: 'center', color: 'var(--ink-3)' }}>{t('வசூல் இல்லை', 'No collections yet')}</td></tr>
                    ) : data.monthly.map((m) => (
                      <tr key={m.due_month} style={{ borderTop: '1px solid var(--line)' }}>
                        <td style={{ padding: '6px 10px' }}>{short_month_label(m.due_month, lang)} {m.due_month.slice(0, 4)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }} className="mono">{format_inr(m.expected_paise)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }} className="mono">{format_inr(m.collected_paise)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--primary)' }} className="mono">{format_inr(m.commission_paise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Cycle plan */}
            <div className="card">
              <div className="card-head"><div className="card-title">{t('சுழற்சி பட்டியல்', 'Cycle plan')}</div></div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead style={{ background: 'var(--bg-elev)' }}>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>#</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('தேதி', 'Date')}</th>
                      <th style={{ textAlign: 'left', padding: '6px 10px', fontWeight: 600 }}>{t('பெறுநர்', 'Recipient')}</th>
                      <th style={{ textAlign: 'right', padding: '6px 10px', fontWeight: 600 }}>{t('பணம்', 'Amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.payouts.length === 0 ? (
                      <tr><td colSpan={4} style={{ padding: 14, textAlign: 'center', color: 'var(--ink-3)' }}>{t('சுழற்சிகள் இல்லை', 'No cycle plan')}</td></tr>
                    ) : data.payouts.map((p) => (
                      <tr key={p.cycle_index} style={{ borderTop: '1px solid var(--line)', background: p.is_disbursed ? 'var(--good-soft)' : undefined }}>
                        <td style={{ padding: '6px 10px' }}>{p.cycle_index}</td>
                        <td style={{ padding: '6px 10px' }} className="mono">{p.cycle_date}</td>
                        <td style={{ padding: '6px 10px' }} className={lang === 'ta' ? 'tamil' : ''}>
                          {p.recipient_subscriber_id ? (
                            <Link to={`/subscribers/${p.recipient_subscriber_id}`} style={{ color: 'var(--ink)', textDecoration: 'none' }}>
                              {(lang === 'ta' ? p.recipient_name_ta : p.recipient_name_en) || '—'}
                            </Link>
                          ) : <span style={{ color: 'var(--ink-3)' }}>{t('ஒதுக்கப்படவில்லை', 'unassigned')}</span>}
                        </td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }} className="mono">{format_inr(p.receive_amount_paise)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {pdf_m.error && <div style={{ color: 'var(--bad)', padding: 10 }}>{friendly_error(pdf_m.error)}</div>}
      {preview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,24,20,0.45)', zIndex: 10000, padding: 16, overflow: 'auto' }}>
          <div style={{ maxWidth: 1100, margin: 'auto', background: 'white', borderRadius: 8 }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>PDF preview (DRY_RUN)</div>
              <button type="button" className="btn sm" onClick={() => set_preview(null)}>Close</button>
            </div>
            <iframe title="pnl-preview" srcDoc={preview} style={{ width: '100%', height: '80vh', border: 0 }} />
          </div>
        </div>
      )}
    </div>
  );
}
